import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { Subscription } from 'rxjs';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { FormFieldConfig } from '@shared/interfaces/config';
import { ValidationObserver } from 'vee-validate';
import { set, get } from 'lodash';
import moment from 'moment';

@Component({
  name: 'bpc-entity-filter-provider',
  components: {
    'validation-observer': ValidationObserver,
  },
})
export default class EntityFilterProvider extends Vue {
  @Prop(String) readonly entity: string;
  @Prop(String) readonly configPath: string;

  @Prop({ type: String, default: 'default' }) readonly filterConfigName: string;
  @Prop(Object) readonly value: AnyEntity;
  @Prop({ type: String, default: 'filter' }) readonly filterType: string;
  @Prop({ type: String, default: 'table' }) readonly storeName: string;
  @Prop({ type: String, default: 'form-inline' }) readonly formClass: string;
  @Prop(String) readonly configFileName: string;

  loadingConfig = true;

  entityConfigSubscription: Subscription;

  fieldsObject: Record<string, FormFieldConfig> = {};

  public get fields(): FormFieldConfig[] {
    return Object.values(this.fieldsObject).sort(this.compareByOrder);
  }

  public get model(): AnyEntity {
    return this.value;
  }

  public set model(val: AnyEntity) {
    this.$emit('input', val);
  }

  public get resolvedConfigFileName(): string {
    return this.configFileName ? this.configFileName : this.entity;
  }

  public get entityFilterFormConfigSlug(): string {
    return this.configPath
      ? this.configPath
      : `${this.resolvedConfigFileName}.filters.${this.filterConfigName}`;
  }

  public compareByOrder(a: FormFieldConfig, b: FormFieldConfig): number {
    if (a.order < b.order) {
      return -1;
    }
    if (a.order > b.order) {
      return 1;
    }
    return 0;
  }

  get filterModel() {
    return this.$store.getters[`${this.storeName}/filterModel`](this.entity) || {};
  }

  public created(): void {
    if (this.filterType === 'filter') {
      this.$store.commit(`${this.storeName}/startLoadingFilters`, this.entity);
    } else if (this.filterType === 'tab-filter') {
      this.$store.commit(`${this.storeName}/startLoadingTabFilters`, this.entity);
    } else if (this.filterType === 'category-filter') {
      this.$store.commit(`${this.storeName}/startLoadingCategoryFilters`, this.entity);
    }

    this.loadingConfig = true;

    if (this.entityConfigSubscription) {
      this.entityConfigSubscription.unsubscribe();
    }

    this.entityConfigSubscription = this.getConfigObservable().subscribe(
      (config: BPCGenericApiResponse) => {
        this.manageConfigResponse(config);
        this.$emit('config-loaded', config.data);
        const initialModel = this.$bpcModelFactory.getInitialFormModel(this.fieldsObject);
        const newModel = Object.keys(initialModel).reduce((result, key) => {
          const isNully =
            initialModel[key] === null ||
            initialModel[key] === undefined ||
            initialModel[key] === '';
          const valueToSet = !isNully ? initialModel[key] : this.filterModel[key] || null;
          this.$set(result, key, valueToSet);
          return result;
        }, {});
        this.model = newModel;
        if (newModel) {
          this.$store.commit(`${this.storeName}/setFilterModel`, {
            entity: this.entity,
            model: newModel,
          });
        }
      },
      error => {
        console.log(error);
      },
      () => {
        if (this.filterType === 'filter') {
          this.$store.commit(`${this.storeName}/stopLoadingFilters`, this.entity);
        } else if (this.filterType === 'tab-filter') {
          this.$store.commit(`${this.storeName}/stopLoadingTabFilters`, this.entity);
        } else if (this.filterType === 'category-filter') {
          this.$store.commit(`${this.storeName}/stopLoadingCategoryFilters`, this.entity);
        }
        this.loadingConfig = false;
      },
    );
  }

  manageConfigResponse(res: BPCGenericApiResponse) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.fieldsObject = res.data ? res.data.fields : {};
  }

  getConfigObservable() {
    return this.$bpcConfig.getEntityConfig(this.entityFilterFormConfigSlug);
  }
}
